import React, { useState, useEffect } from "react";
import {
  Popover,
  Grid,
  makeStyles,
  useTheme,
  useMediaQuery,
  Fade,
  Grow,
} from "@material-ui/core";
import { Add as AddIcon } from "@material-ui/icons";
import MessageActionButton from "../MessageActionButton";
import RippleEffect from "../RippleEffect";

// 🎯 Estilos avançados com animações escalonadas
const useStyles = makeStyles((theme) => ({
  // 🎭 Keyframes para animações
  "@keyframes bounceIn": {
    "0%": {
      opacity: 0,
      transform: "scale(0.3)",
    },
    "50%": {
      opacity: 1,
      transform: "scale(1.05)",
    },
    "70%": {
      transform: "scale(0.9)",
    },
    "100%": {
      opacity: 1,
      transform: "scale(1)",
    },
  },

  "@keyframes slideInRight": {
    "0%": {
      opacity: 0,
      transform: "translateX(30px)",
    },
    "100%": {
      opacity: 1,
      transform: "translateX(0)",
    },
  },

  "@keyframes float": {
    "0%, 100%": {
      transform: "translateY(0px)",
    },
    "50%": {
      transform: "translateY(-2px)",
    },
  },

  // 🎯 Container principal do popover
  reactionPopover: {
    "& .MuiPaper-root": {
      borderRadius: "25px",
      backgroundColor: "rgba(255, 255, 255, 0.95)",
      backdropFilter: "blur(10px)",
      border: "1px solid rgba(0, 0, 0, 0.08)",
      boxShadow: "0 8px 32px rgba(0, 0, 0, 0.12)",
      padding: "8px 12px",
      minWidth: "280px",
      overflow: "visible",
      
      // Tema escuro
      [theme.palette.type === "dark" && "@media"]: {
        backgroundColor: "rgba(45, 55, 72, 0.95)",
        borderColor: "rgba(255, 255, 255, 0.1)",
      },

      // Seta do popover
      "&::before": {
        content: '""',
        position: "absolute",
        top: "100%",
        left: "50%",
        transform: "translateX(-50%)",
        width: 0,
        height: 0,
        borderLeft: "8px solid transparent",
        borderRight: "8px solid transparent",
        borderTop: "8px solid rgba(255, 255, 255, 0.95)",
        filter: "drop-shadow(0 2px 4px rgba(0, 0, 0, 0.1))",
      },
    },
  },

  // 🎯 Container das reações
  reactionsContainer: {
    display: "flex",
    alignItems: "center",
    gap: "4px",
    padding: "4px 0",
  },

  // 🎯 Botão de reação individual
  reactionButton: {
    width: "40px",
    height: "40px",
    borderRadius: "50%",
    backgroundColor: "transparent",
    border: "none",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    fontSize: "24px",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    position: "relative",
    overflow: "hidden",
    
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.08)",
      transform: "scale(1.2)",
      animation: "$float 1s ease-in-out infinite",
    },
    
    "&:active": {
      transform: "scale(0.9)",
      transition: "all 0.1s ease",
    },
    
    // Animação de entrada escalonada
    "&.animate-in": {
      animation: "$bounceIn 0.6s ease-out",
    },
    
    // Delay escalonado para cada emoji
    "&.delay-1": { animationDelay: "0.05s" },
    "&.delay-2": { animationDelay: "0.1s" },
    "&.delay-3": { animationDelay: "0.15s" },
    "&.delay-4": { animationDelay: "0.2s" },
    "&.delay-5": { animationDelay: "0.25s" },
    "&.delay-6": { animationDelay: "0.3s" },
  },

  // 🎯 Botão "+" (Mais reações)
  moreButton: {
    width: "36px",
    height: "36px",
    borderRadius: "50%",
    backgroundColor: "rgba(0, 0, 0, 0.06)",
    border: "1px solid rgba(0, 0, 0, 0.12)",
    cursor: "pointer",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    color: "#65676b",
    transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
    marginLeft: "4px",
    
    "&:hover": {
      backgroundColor: "rgba(0, 0, 0, 0.12)",
      transform: "scale(1.1) rotate(90deg)",
      color: "#42526e",
    },
    
    "&:active": {
      transform: "scale(0.95) rotate(90deg)",
      transition: "all 0.1s ease",
    },
    
    // Animação de entrada
    "&.animate-in": {
      animation: "$slideInRight 0.5s ease-out",
      animationDelay: "0.35s",
      animationFillMode: "both",
    },
    
    "& .MuiSvgIcon-root": {
      fontSize: "18px",
    },
  },

  // 🎯 Estados responsivos
  [theme.breakpoints.down("xs")]: {
    reactionButton: {
      width: "36px",
      height: "36px",
      fontSize: "20px",
    },
    
    moreButton: {
      width: "32px",
      height: "32px",
      
      "& .MuiSvgIcon-root": {
        fontSize: "16px",
      },
    },
  },
}));

// 🎯 Emojis de reação padrão (WhatsApp style)
const DEFAULT_REACTIONS = ["❤️", "😂", "😮", "😢", "😡", "👍"];

// 🎯 Componente AnimatedReactionPicker
const AnimatedReactionPicker = ({
  anchorEl,
  open,
  onClose,
  onReactionSelect,
  onMoreReactions,
  reactions = DEFAULT_REACTIONS,
  placement = "top",
  animate = true,
}) => {
  const classes = useStyles();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("xs"));
  const [animateButtons, setAnimateButtons] = useState(false);

  // ✨ Trigger animações de entrada
  useEffect(() => {
    if (open && animate) {
      const timer = setTimeout(() => setAnimateButtons(true), 100);
      return () => clearTimeout(timer);
    } else {
      setAnimateButtons(false);
    }
  }, [open, animate]);

  // 🎯 Handler de seleção de reação
  const handleReactionClick = (reaction, event) => {
    if (onReactionSelect) {
      onReactionSelect(reaction, event);
    }
    onClose();
  };

  // 🎯 Handler do botão "mais reações"
  const handleMoreClick = (event) => {
    if (onMoreReactions) {
      onMoreReactions(event);
    }
  };

  return (
    <Popover
      open={open}
      anchorEl={anchorEl}
      onClose={onClose}
      className={classes.reactionPopover}
      anchorOrigin={{
        vertical: placement === "top" ? "top" : "bottom",
        horizontal: "center",
      }}
      transformOrigin={{
        vertical: placement === "top" ? "bottom" : "top",
        horizontal: "center",
      }}
      TransitionComponent={Grow}
      transitionDuration={300}
    >
      <div className={classes.reactionsContainer}>
        {reactions.map((reaction, index) => (
          <Fade
            key={reaction}
            in={open}
            timeout={300}
            style={{ transitionDelay: animate ? `${index * 50}ms` : "0ms" }}
          >
            <button
              className={`${classes.reactionButton} ${
                animateButtons ? "animate-in" : ""
              } delay-${index + 1}`}
              onClick={(e) => handleReactionClick(reaction, e)}
              aria-label={`Reagir com ${reaction}`}
            >
              {reaction}
              <RippleEffect 
                trigger={null} 
                color="rgba(0, 0, 0, 0.1)" 
              />
            </button>
          </Fade>
        ))}
        
        {/* Botão "+" para mais reações */}
        <Fade
          in={open}
          timeout={300}
          style={{ 
            transitionDelay: animate ? `${reactions.length * 50 + 50}ms` : "0ms" 
          }}
        >
          <button
            className={`${classes.moreButton} ${
              animateButtons ? "animate-in" : ""
            }`}
            onClick={handleMoreClick}
            aria-label="Mais reações"
          >
            <AddIcon />
            <RippleEffect 
              trigger={null} 
              color="rgba(0, 0, 0, 0.1)" 
            />
          </button>
        </Fade>
      </div>
    </Popover>
  );
};

export default AnimatedReactionPicker;
